// Report Status Enum
export enum ReportStatus {
  NEW = 'NEW',
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  SIGNED = 'SIGNED',
  CLOSED = 'CLOSED',
  CANCELED = 'CANCELED'
}

// T-017: Billing Status Enum, separato dalla state machine del report
export enum BillingStatus {
  TO_VALIDATE = 'TO_VALIDATE',
  VALIDATED = 'VALIDATED',
  INVOICED = 'INVOICED'
}

// Service Type Enum
export enum ServiceType {
  TYPE1 = 'TYPE1',
  TYPE2 = 'TYPE2',
  TYPE3 = 'TYPE3',
  TYPE4 = 'TYPE4'
}

// ============================================
// Authentication Types (from new API)
// ============================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  user: UserInfo;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  tokenType: string;
}

// User Info from login response
export interface UserInfo {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  scope: string;
  agentId?: string;
  loginCount: number;
  lastLoginAt?: string;
  passportEnabled?: boolean;
  analyticsEnabled?: boolean;
}

// Auth state stored in localStorage
export interface AuthState {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  user: UserInfo;
}

// ============================================
// Customer Types (updated for new API)
// ============================================

export interface Customer {
  id: string;
  agentId?: string;
  code?: string;
  name: string;
  groupCode?: string;
  city?: string;
  countryCode?: string;
  address?: string;
  vatNumber?: string;
  email?: string;
  phone?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerRequest {
  agentId?: string;
  code?: string;
  name: string;
  groupCode?: string;
  city?: string;
  countryCode?: string;
  address?: string;
  vatNumber?: string;
  email?: string;
  phone?: string;
  notes?: string;
  isActive: boolean;
}

// ============================================
// Agent Types (new - replaces Technician concept)
// ============================================

export interface Agent {
  id: string;
  code: string;
  name: string;
  imageBase64: string;
  countryCode?: string;
  supportEmail?: string;
  purchasingEmail?: string;
  administrationEmail?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// User Types (for user management)
// ============================================

export interface UserResponse {
  id: string;
  agentId?: string;
  email: string;
  roleId: string;
  roleCode: string;
  roleName: string;
  scope: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isActive: boolean;
  lastLoginAt?: string;
  loginCount: number;
  createdAt: string;
  updatedAt: string;
}

// Technician is now a User with TECHNICIAN role
export interface Technician {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  // Computed full name
  name?: string;
}

// ============================================
// Report Time Log Types
// ============================================

export interface ReportTimeLog {
  id: string;
  reportMachineId: string;
  logDate: string;
  startTime: string;
  finishTime: string;
  breakTime?: number;
  travelTime?: number;
  travelKm?: number;
  createdAt: string;
  updatedAt: string;
}

// Report Time Log Input (for forms)
export interface ReportTimeLogInput {
  id?: string;
  reportMachineId?: string;
  logDate: string;
  startTime: string;
  finishTime: string;
  breakTime?: number;
  travelTime?: number;
  travelKm?: number;
}

export interface ReportTimeLogRequest {
  reportMachineId: string;
  logDate: string;
  startTime: string;
  finishTime: string;
  breakTime?: number;
  travelTime?: number;
  travelKm?: number;
}

// ============================================
// Report Spare Part Types
// ============================================

export interface ReportSparePart {
  id?: string;
  reportMachineId: string;
  idSparePart: string;
  partNumber: string;
  descriptionIt: string;
  descriptionEn?: string;
  quantity: number;
  used: number;
  warranty?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReportSparePartRequest {
  reportMachineId: string;
  idSparePart: string;
  descriptionIt: string;
  descriptionEn?: string;
  quantity: number;
  used: number;
  warranty?: boolean;
}

export interface CsvRowError {
  rowNumber: number;
  errorCode: 'missing_product_code' | 'invalid_quantity' | 'part_not_found' | 'ambiguous_part_number' | 'ambiguous_serial_number' | 'insert_failed';
  errorMessage: string;
}

export interface CsvImportResult {
  totalRows: number;
  insertedCount: number;
  errorCount: number;
  errors: CsvRowError[];
}

// ============================================
// Multi-Machine CSV Import Types
// ============================================

export interface MultiImportRowSuccess {
  rowNumber: number;
  reportMachineId: string;
  machineId: string | null;
  warningCode: 'machine_added_to_report' | null;
}

export interface MultiImportRowError {
  rowNumber: number;
  errorCode: 'missing_serial_number' | 'missing_product_code' | 'invalid_quantity' | 'machine_not_found' | 'part_not_found' | 'ambiguous_part_number' | 'ambiguous_serial_number' | 'insert_failed';
  errorMessage: string;
  serialNumber: string | null;
}

export interface MultiMachineImportResult {
  totalRows: number;
  insertedCount: number;
  errorCount: number;
  rows: MultiImportRowSuccess[];
  errors: MultiImportRowError[];
}

/**
 * PROD-499: import ricambi da documento Panthera. La richiesta e' asincrona (Pub/Sub verso il
 * servizio interno Proform): si crea con POST e se ne interroga lo stato finche' non e' conclusa.
 */
export type PantheraImportStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'NOT_FOUND'
  | 'EMPTY'
  | 'FAILED'
  | 'EXPIRED'
  /** elaborazione iniziata e mai conclusa: righe forse gia' scritte, il documento resta bloccato */
  | 'INTERRUPTED';

export interface PantheraImportMachineSummary {
  serialNumber: string;
  reportMachineId: string;
  rowCount: number;
  addedToReport: boolean;
}

export interface PantheraImport {
  id: string;
  reportId: string;
  documentNumber: string;
  status: PantheraImportStatus;
  /** true quando il documento e' gia' importato (o in import) sul report: e' la richiesta esistente */
  duplicate: boolean;
  requestedAt: string;
  requestedByName?: string;
  completedAt?: string;
  pantheraDocument?: string;
  documentDate?: string;
  totalRows?: number;
  insertedCount?: number;
  errorCount?: number;
  errorCode?: string;
  errorMessage?: string;
  result?: MultiMachineImportResult;
  machines?: PantheraImportMachineSummary[];
}

// ============================================
// Report Machine Types
// ============================================

/**
 * T-013: un guasto estratto dall'LLM. `*LabelIt`/`*LabelEn` sono l'etichetta di tassonomia da
 * mostrare in UI (assente se il codice non e' stato risolto); `*Raw`/`*Code` sono il dettaglio
 * (testo grezzo del report, codice interno) da mostrare solo come tooltip, non come testo
 * principale — vedi src/utils/failureClassification.ts.
 */
export interface MachineFailureClassification {
  componentRaw?: string;
  symptomRaw?: string;
  causeRaw?: string;
  actionRaw?: string;
  componentCode?: string;
  symptomCode?: string;
  causeCode?: string;
  actionCode?: string;
  componentLabelIt?: string;
  componentLabelEn?: string;
  symptomLabelIt?: string;
  symptomLabelEn?: string;
  causeLabelIt?: string;
  causeLabelEn?: string;
  actionLabelIt?: string;
  actionLabelEn?: string;
}

export interface ReportMachine {
  id: string;
  reportId: string;
  idMachine: string;
  idNumber: string;
  description: string;
  serviceType: string;
  problemDescription?: string;
  workDescription?: string;
  futureWorksDescription?: string;
  customerReference?: string;
  softwareVersion?: string;
  osVersion?: string;
  followUpAfterService?: string;
  manuallyAdded: boolean;
  spareParts?: ReportSparePart[];
  timeLogs?: ReportTimeLog[];
  createdAt: string;
  updatedAt: string;
  /** Sempre vuoto finche' il report non e' CLOSED. */
  failureClassifications?: MachineFailureClassification[];
}

export interface ReportMachineRequest {
  reportId: string;
  idMachine: string;
  idNumber: string;
  description: string;
  serviceType: string;
  problemDescription?: string;
  workDescription?: string;
  futureWorksDescription?: string;
  customerReference?: string;
  softwareVersion?: string;
  osVersion?: string;
  followUpAfterService?: string;
  manuallyAdded: boolean;
}

// ============================================
// Attachment Types
// ============================================

export interface Attachment {
  id: string;
  reportId: string;
  fileName: string;
  note?: string;
  content: string; // base64 encoded
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentRequest {
  reportId: string;
  fileName: string;
  note?: string;
  content: string;
}

// ============================================
// Travel Time Types
// ============================================

export interface TravelLeg {
  date?: string;
  departureTime?: string;
  arrivalTime?: string;
  nextDay?: boolean;
  breakTime?: number;
  km?: number;
}

export interface TravelTimeData {
  outbound?: TravelLeg;
  inbound?: TravelLeg;
}

// ============================================
// Service Location Types
// ============================================

export interface ServiceLocation {
  description?: string;
  address?: string;
  city?: string;
  country?: string;
}

// ============================================
// Report Types
// ============================================

export interface Report {
  id: string;
  number: string;
  creation: string;
  status: string;
  agentId?: string;
  customerId: string;
  customerName?: string;
  customerCountryCode?: string;
  technicianId: string;
  technicianName?: string;
  supportTechnicianId?: string;
  supportTechnicianName?: string;
  travelTime?: number;
  travelKm?: number;
  workTime?: number;
  startTime?: string;
  finishTime?: string;
  travelTimeData?: TravelTimeData;
  serviceLocation?: ServiceLocation;
  machines?: ReportMachine[];
  attachments?: Attachment[];
  createdAt: string;
  updatedAt: string;
  /** T-017: null se il report non e' billing-eligible o la riga report_billing non esiste ancora. */
  billingStatus?: string;
  commercialReference?: string;
}

// ============================================
// Billing Types (T-017)
// ============================================

export interface ReportBilling {
  id: string;
  reportId: string;
  billingStatus: string | null;
  commercialReference: string | null;
  registeredWorkMinutes: number | null;
  registeredTravelMinutes: number | null;
  registeredKm: number | null;
  registeredAt: string | null;
  billedWorkMinutes: number | null;
  billedTravelMinutes: number | null;
  billedKm: number | null;
  adjustmentReason: string | null;
  validatedBy: string | null;
  validatedAt: string | null;
  invoicedBy: string | null;
  invoicedAt: string | null;
  notes: string | null;
  notesCreatedBy: string | null;
  notesCreatedAt: string | null;
  notesUpdatedBy: string | null;
  notesUpdatedAt: string | null;
  editable: boolean;
}

export interface UpdateReportBillingRequest {
  commercialReference?: string;
  billedWorkMinutes?: number;
  billedTravelMinutes?: number;
  billedKm?: number;
  adjustmentReason?: string;
  notes?: string;
}

export interface UpdateBillingStatusRequest {
  status: BillingStatus;
}

export interface BillingHistoryEvent {
  id: string;
  operation: string;
  oldValue: string | null;
  newValue: string | null;
  userId: string | null;
  userEmail: string | null;
  occurredAt: string;
}

/** Body opzionale di PATCH /reports/{id}/status: i totali calcolati da calculateReportTotals. */
export interface ClosureSnapshot {
  registeredWorkMinutes: number;
  registeredTravelMinutes: number;
  registeredKm: number;
}

export interface ReportRequest {
  number: string;
  creation: string;
  status: string;
  agentId?: string;
  customerId: string;
  technicianId: string;
  supportTechnicianId?: string;
  travelTime?: number;
  travelKm?: number;
  workTime?: number;
  startTime?: string;
  finishTime?: string;
  travelTimeData?: TravelTimeData;
  serviceLocation?: ServiceLocation;
}

export interface ReportUpdateRequest {
  number?: string;
  creation?: string;
  status?: string;
  agentId?: string;
  customerId?: string;
  technicianId?: string;
  supportTechnicianId?: string;
  travelTime?: number;
  travelKm?: number;
  workTime?: number;
  startTime?: string;
  finishTime?: string;
  travelTimeData?: TravelTimeData;
  serviceLocation?: ServiceLocation;
}

// ============================================
// Machine Types (installed machines)
// ============================================

export type MachineVisibility = 'public' | 'private';

export interface Machine {
  id: string;
  agentOwnerId?: string;
  customerId?: string;
  machineModelId?: string;
  serialNumber?: string;
  descriptionPublic?: string;
  descriptionOwner?: string;
  displayDescription?: string;
  visibility: MachineVisibility;
  installationDate?: string;
  status: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Machine Model Types (catalog)
// ============================================

export interface MachineModel {
  id: string;
  manufacturer?: string;
  modelCode: string;
  modelName: string;
  family?: string;
  imageBase64: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Spare Part Types (catalog)
// ============================================

export interface SparePart {
  id: string;
  partNumber: string;
  productCode?: string;
  descriptionIt: string;
  descriptionEn?: string;
  unit?: string;
  imageBytes?: string; // base64 encoded
  imagePath?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Role Types
// ============================================

export interface Role {
  id: string;
  code: string;
  name: string;
  scope: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Signature Configuration
// ============================================

export enum SignatureMethod {
  UPLOAD_SIGNATURE = 'UPLOAD_SIGNATURE',
  CANVAS_SIGNATURE = 'CANVAS_SIGNATURE',
  UPLOAD_AND_CANVAS = 'UPLOAD_AND_CANVAS'
}

// ============================================
// Filter Interface for MainForm
// ============================================

export interface ReportFilter {
  searchText?: string;
  reportNumber?: string;
  customerName?: string;
  technicianName?: string;
  machineName?: string;
  sparePartName?: string;
  showNew: boolean;
  showReady: boolean;
  showInProgress: boolean;
  showSigned: boolean;
  showClosed: boolean;
  showCanceled: boolean;
  /** T-017: toggle indipendenti sullo stato di fatturazione, stesso pattern di showNew/showReady/... */
  showBillingToValidate?: boolean;
  showBillingValidated?: boolean;
  showBillingInvoiced?: boolean;
  showBillingNone?: boolean;
  /** T-017: ricerca su report_billing.commercial_reference (home Finance). */
  commercialReference?: string;
}

// ============================================
// Pagination Types
// ============================================

export interface PageRequest {
  page?: number;
  size?: number;
  sort?: string[];
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// ============================================
// Legacy Types (for backward compatibility during migration)
// ============================================

// User Role Enum (kept for compatibility)
export enum UserRole {
  ADMIN = 'ADMIN',
  AGENT = 'AGENT',
  TECHNICIAN = 'TECHNICIAN',
  SUPER_ADMIN = 'SUPER_ADMIN',
  FREELANCE = 'FREELANCE'
}

// Legacy User Interface (now use UserInfo instead)
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  agentId?: string;
}
